import { getCognitoIdentityProvider } from '@utils/federatedAuth';
import type { FederatedProvider } from '@utils/federatedAuth';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import {
  COGNITO_CALLBACK_URL,
  COGNITO_CLIENT_ID,
  COGNITO_DOMAIN,
} from '@config/config';
import LocalStorageKey from '@enums/LocalStorageKey';
import LocalStorageService from '@services/LocalStorageService';

// Remember which provider initiated the redirect so the post-callback signup
// success handler can tag the conversion (method: 'google' | 'linkedin').
const rememberFederatedProvider = (provider: FederatedProvider) => {
  LocalStorageService.setItem(
    LocalStorageKey.FEDERATED_SIGNUP_PROVIDER,
    provider.toLowerCase(),
  );
};

export interface FederatedSignInContextValue {
  /** Initiates federated sign-in for the given provider (Google or LinkedIn). */
  signInWith: (provider: FederatedProvider) => void;
}

const FederatedSignInContext =
  createContext<FederatedSignInContextValue | null>(null);

/**
 * Provider that uses react-oidc-context to initiate federated sign-in.
 * Must be rendered inside react-oidc-context's AuthProvider.
 */
export const FederatedSignInProviderWithOidc = ({
  children,
}: {
  children: ReactNode;
}) => {
  const auth = useOidcAuth();

  const signInWith = useCallback(
    (provider: FederatedProvider) => {
      rememberFederatedProvider(provider);
      auth.signinRedirect({
        extraQueryParams: {
          identity_provider: getCognitoIdentityProvider(provider),
          prompt: 'select_account',
        },
      });
    },
    [auth],
  );

  const value = useMemo<FederatedSignInContextValue>(
    () => ({ signInWith }),
    [signInWith],
  );

  return (
    <FederatedSignInContext.Provider value={value}>
      {children}
    </FederatedSignInContext.Provider>
  );
};

/**
 * Provider that initiates federated sign-in via direct URL redirect.
 * Use when react-oidc-context is not configured (e.g. missing Cognito env).
 */
export const FederatedSignInProviderFallback = ({
  children,
}: {
  children: ReactNode;
}) => {
  const signInWith = useCallback((provider: FederatedProvider) => {
    rememberFederatedProvider(provider);
    const params = new URLSearchParams({
      identity_provider: getCognitoIdentityProvider(provider),
      response_type: 'code',
      client_id: COGNITO_CLIENT_ID,
      redirect_uri: COGNITO_CALLBACK_URL,
      scope: 'openid email profile aws.cognito.signin.user.admin',
      prompt: 'select_account',
    });
    window.location.href = `${COGNITO_DOMAIN}/oauth2/authorize?${params.toString()}`;
  }, []);

  const value = useMemo<FederatedSignInContextValue>(
    () => ({ signInWith }),
    [signInWith],
  );

  return (
    <FederatedSignInContext.Provider value={value}>
      {children}
    </FederatedSignInContext.Provider>
  );
};

export const useFederatedSignIn = (): FederatedSignInContextValue => {
  const context = useContext(FederatedSignInContext);
  if (context === null) {
    throw new Error(
      'useFederatedSignIn must be used within a FederatedSignIn provider',
    );
  }
  return context;
};
